import LockerRoomDrawingBenchesService from '@/endpoints/lockerRoomDrawingBenchesService';
import LockerRoomDrawingConfigurationsService from '@/endpoints/lockerRoomDrawingConfigurationsService';
import LockerRoomDrawingWallsService from '@/endpoints/lockerRoomDrawingWallsService';
import { RoomDesignerService } from '@/endpoints/roomDesignerService';
import { HttpStatusCode } from '@/enums/httpCodes';
import ArrayHelper from '@/helpers/arrayHelper';
import { LockerRoomDrawingConfiguration } from '@/models/lockers/lockerRoomDrawingConfiguration';
import RdDrawingNote, { RdDrawingNoteDeleteRequest, RdDrawingNoteRequest } from '@/models/roomDesigner/rdDrawingNote';
import {
  LockerRoomDrawingBench,
  LockerRoomDrawingBenchRequest,
  LockerRoomDrawingBenchTransformUpdate,
} from '@/models/lockers/lockerRoomDrawingBench';
import {
  LockerRoomDrawingWall,
  LockerRoomDrawingWallRequest,
  LockerRoomDrawingWallTransformUpdate,
  LockerRoomDrawingWallUpdateRequest,
} from '@/models/lockers/lockerRoomDrawingWall';
import { Module, VuexModule, action, mutation } from 'vuex-class-component';

@Module()
export class LockerRoomDrawingModule extends VuexModule {
  private _isSubmitting = false;
  private _wallsByRoomId: Record<string, LockerRoomDrawingWall[]> = {};
  private _benchesByRoomId: Record<string, LockerRoomDrawingBench[]> = {};
  private _configurationsByRoomId: Record<string, LockerRoomDrawingConfiguration | null> = {};
  private _notesByRoomId: Record<string, RdDrawingNote[]> = {};
  private _noteLoadingCount = 0;

  public get isSubmitting(): boolean {
    return this._isSubmitting;
  }

  public get noteIsLoading(): boolean {
    return this._noteLoadingCount > 0;
  }

  public get getNotesForRoom() {
    return (roomId: string): RdDrawingNote[] => this._notesByRoomId[roomId] ?? [];
  }

  public get getWallsForRoom() {
    return (roomId: string): LockerRoomDrawingWall[] => this._wallsByRoomId[roomId] ?? [];
  }

  public get getBenchesForRoom() {
    return (roomId: string): LockerRoomDrawingBench[] => this._benchesByRoomId[roomId] ?? [];
  }

  public get getConfigurationForRoom() {
    return (roomId: string): LockerRoomDrawingConfiguration | null => this._configurationsByRoomId[roomId] ?? null;
  }

  @mutation private setSubmitting(value: boolean) {
    this._isSubmitting = value;
  }

  @mutation private setWallsForRoom(payload: { roomId: string; walls: LockerRoomDrawingWall[] }) {
    this._wallsByRoomId = { ...this._wallsByRoomId, [payload.roomId]: payload.walls };
  }

  @mutation private setBenchesForRoom(payload: { roomId: string; benches: LockerRoomDrawingBench[] }) {
    this._benchesByRoomId = { ...this._benchesByRoomId, [payload.roomId]: payload.benches };
  }

  @mutation public setNotesForRoom(payload: { roomId: string; notes: RdDrawingNote[] }) {
    this._notesByRoomId = { ...this._notesByRoomId, [payload.roomId]: payload.notes };
  }

  @mutation private removeNoteForRoom(payload: { roomId: string; noteId: string }) {
    this._notesByRoomId = {
      ...this._notesByRoomId,
      [payload.roomId]: (this._notesByRoomId[payload.roomId] ?? []).filter(note => note.id !== payload.noteId),
    };
  }

  @mutation private addNoteLoading() {
    this._noteLoadingCount++;
  }

  @mutation private removeNoteLoading() {
    this._noteLoadingCount = Math.max(0, this._noteLoadingCount - 1);
  }

  // Public: the room creation modal seeds the cache from the values it just
  // sent, so a freshly created room never triggers the GET.
  @mutation public setConfigurationForRoom(payload: {
    roomId: string;
    configuration: LockerRoomDrawingConfiguration | null;
  }) {
    this._configurationsByRoomId = { ...this._configurationsByRoomId, [payload.roomId]: payload.configuration };
  }

  @action({ mode: 'mutate' })
  public async fetchWallsForRoom(roomId: string): Promise<void> {
    const walls = await LockerRoomDrawingWallsService.getWallsForRoom(roomId);
    this.setWallsForRoom({ roomId, walls });
  }

  @action({ mode: 'mutate' })
  public async fetchBenchesForRoom(roomId: string): Promise<void> {
    const benches = await LockerRoomDrawingBenchesService.getBenchesForRoom(roomId);
    this.setBenchesForRoom({ roomId, benches });
  }

  // At most one GET per room per session: the cached entry (null included)
  // short-circuits the request.
  @action({ mode: 'mutate' })
  public async fetchConfigurationForRoom(roomId: string): Promise<LockerRoomDrawingConfiguration | null> {
    if (roomId in this._configurationsByRoomId) {
      return this._configurationsByRoomId[roomId];
    }
    const configuration = await LockerRoomDrawingConfigurationsService.getForRoom(roomId);
    this.setConfigurationForRoom({ roomId, configuration });
    return configuration;
  }

  @action({ mode: 'mutate' })
  public async updateConfigurationForRoom(payload: {
    roomId: string;
    request: LockerRoomDrawingConfiguration;
  }): Promise<void> {
    await LockerRoomDrawingConfigurationsService.updateForRoom(payload.roomId, payload.request);
    this.setConfigurationForRoom({ roomId: payload.roomId, configuration: payload.request });
  }

  @action({ mode: 'mutate' })
  public async createNote(request: RdDrawingNoteRequest): Promise<RdDrawingNote | null> {
    this.addNoteLoading();
    try {
      const notes = await RoomDesignerService.postDrawingNotes(request);
      const drawingNotes = [...this.getNotesForRoom(request.roomId)];
      ArrayHelper.addOrReplace(drawingNotes, notes);
      this.setNotesForRoom({ roomId: request.roomId, notes: drawingNotes });
      return notes[0] ?? null;
    } finally {
      this.removeNoteLoading();
    }
  }

  @action({ mode: 'mutate' })
  public async updateNote(request: RdDrawingNoteRequest): Promise<void> {
    this.addNoteLoading();
    try {
      const note = await RoomDesignerService.putDrawingNotes(request);
      const drawingNotes = this.getNotesForRoom(request.roomId).map(x => (x.id === note.id ? note : x));
      this.setNotesForRoom({ roomId: request.roomId, notes: drawingNotes });
    } catch (exception: any) {
      if (exception?.response?.status === HttpStatusCode.NotFound && request.id) {
        this.removeNoteForRoom({ roomId: request.roomId, noteId: request.id });
      }
      throw exception;
    } finally {
      this.removeNoteLoading();
    }
  }

  @action({ mode: 'mutate' })
  public async deleteNote(request: RdDrawingNoteDeleteRequest): Promise<void> {
    this.addNoteLoading();
    try {
      await RoomDesignerService.deleteDrawingNotes(request);
      this.removeNoteForRoom({ roomId: request.roomId, noteId: request.id });
    } catch (exception: any) {
      if (exception?.response?.status === HttpStatusCode.NotFound) {
        this.removeNoteForRoom({ roomId: request.roomId, noteId: request.id });
      }
      throw exception;
    } finally {
      this.removeNoteLoading();
    }
  }

  @action({ mode: 'mutate' })
  public async deleteBench(payload: { benchId: number; roomId: string }): Promise<LockerRoomDrawingBench[]> {
    this.setSubmitting(true);
    try {
      const benches = await LockerRoomDrawingBenchesService.deleteBench(payload.benchId);
      this.setBenchesForRoom({ roomId: payload.roomId, benches });
      return benches;
    } finally {
      this.setSubmitting(false);
    }
  }

  @action({ mode: 'mutate' })
  public async duplicateBench(payload: { benchId: number; roomId: string }): Promise<LockerRoomDrawingBench[]> {
    this.setSubmitting(true);
    try {
      const benches = await LockerRoomDrawingBenchesService.duplicateBench(payload.benchId);
      this.setBenchesForRoom({ roomId: payload.roomId, benches });
      return benches;
    } finally {
      this.setSubmitting(false);
    }
  }

  @action({ mode: 'mutate' })
  public async createWall(request: LockerRoomDrawingWallRequest): Promise<LockerRoomDrawingWall[]> {
    this.setSubmitting(true);
    try {
      const walls = await LockerRoomDrawingWallsService.createWall(request);
      this.setWallsForRoom({ roomId: request.roomId, walls });
      return walls;
    } finally {
      this.setSubmitting(false);
    }
  }

  @action({ mode: 'mutate' })
  public async deleteWall(payload: { wallId: number; roomId: string }): Promise<LockerRoomDrawingWall[]> {
    this.setSubmitting(true);
    try {
      const walls = await LockerRoomDrawingWallsService.deleteWall(payload.wallId);
      this.setWallsForRoom({ roomId: payload.roomId, walls });
      return walls;
    } finally {
      this.setSubmitting(false);
    }
  }

  @action({ mode: 'mutate' })
  public async duplicateWall(payload: { wallId: number; roomId: string }): Promise<LockerRoomDrawingWall[]> {
    this.setSubmitting(true);
    try {
      const walls = await LockerRoomDrawingWallsService.duplicateWall(payload.wallId);
      this.setWallsForRoom({ roomId: payload.roomId, walls });
      return walls;
    } finally {
      this.setSubmitting(false);
    }
  }

  @action({ mode: 'mutate' })
  public async updateWall(payload: {
    wallId: number;
    roomId: string;
    request: LockerRoomDrawingWallUpdateRequest;
  }): Promise<LockerRoomDrawingWall[]> {
    this.setSubmitting(true);
    try {
      const walls = await LockerRoomDrawingWallsService.updateWall(payload.wallId, payload.request);
      this.setWallsForRoom({ roomId: payload.roomId, walls });
      return walls;
    } finally {
      this.setSubmitting(false);
    }
  }

  // Deliberately no store write-back: the canvas is the source of the new
  // transforms and already displays them, and no other consumer reads wall
  // transforms mid-session (footer/elements derive from the sequence chain).
  // Writing them back would only re-trigger the canvas walls watcher and
  // rebuild the drawing for nothing. The store re-syncs on the next
  // fetchWallsForRoom (every room load).
  @action({ mode: 'mutate' })
  public async updateWallTransformsForRoom(payload: {
    roomId: string;
    updates: LockerRoomDrawingWallTransformUpdate[];
  }): Promise<void> {
    if (payload.updates.length === 0) {
      return;
    }

    await LockerRoomDrawingWallsService.updateWallTransforms({
      roomId: payload.roomId,
      updates: payload.updates,
    });
  }

  @action({ mode: 'mutate' })
  public async createBench(request: LockerRoomDrawingBenchRequest): Promise<LockerRoomDrawingBench[]> {
    this.setSubmitting(true);
    try {
      const benches = await LockerRoomDrawingBenchesService.createBench(request);
      this.setBenchesForRoom({ roomId: request.roomId, benches });
      return benches;
    } finally {
      this.setSubmitting(false);
    }
  }

  // No store write-back, same rationale as updateWallTransformsForRoom: the
  // canvas already shows the new transforms and re-syncs on the next
  // fetchBenchesForRoom (every room load).
  @action({ mode: 'mutate' })
  public async updateBenchTransformsForRoom(payload: {
    roomId: string;
    updates: LockerRoomDrawingBenchTransformUpdate[];
  }): Promise<void> {
    if (payload.updates.length === 0) {
      return;
    }

    await LockerRoomDrawingBenchesService.updateBenchTransforms({
      roomId: payload.roomId,
      updates: payload.updates,
    });
  }
}
